import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UserSimulationFilter {
  userIds?: string[];
  modules?: string[];
  simulationIds?: string[];
  status?: string[];
}

export interface UserSimulationLogModel {
  event: string;
  payload?: any;
}

export interface UserSimulationEvaluationRequest {
  userSimulationId: string;
  lang: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserSimulationsService {
  private readonly baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = `${environment.apiUrl}/user-simulations`;
  }

  public getUserSimulations = (filter: UserSimulationFilter): Observable<any[]> => {
    if (!filter.status) {
      filter.status = ['FINISHED'];
    }
    return this.http.get<any[]>(`${this.baseUrl}`, { params: { ...filter } });
  };


  public getUserSimulation = (id: string): Observable<any> => {
    return this.http.get<any>(`${this.baseUrl}/${encodeURIComponent(id)}`);
  };

  public getUserSimulationProperty = (id: string, property: string): Observable<{ data: any }> => {
    return this.http.get<{ data: any }>(`${this.baseUrl}/${encodeURIComponent(id)}/property`, { params: { property } });
  };

  public updateUserSimulation = (id: string, payload: any): Observable<void> => {
    return this.http.put<void>(`${this.baseUrl}/${encodeURIComponent(id)}`, payload);
  };


  public insertUserSimulation = (payload: any): Observable<string> =>
    this.http.post(`${this.baseUrl}`, payload, { responseType: 'text' });

  public processUserSimulationEvaluation = (payload: UserSimulationEvaluationRequest): Observable<void> =>
    this.http.post<void>(`${this.baseUrl}/evaluation`, payload);

  public addUserSimulationLog = (id: string, payload: UserSimulationLogModel): Observable<void> => {
    return this.http.patch<void>(`${this.baseUrl}/${encodeURIComponent(id)}/logs`, payload);
  };

  /**
   * Señal de vida del intento en curso: refresca `lastSeenAt` sin agregar nada
   * al array de logs. El sweeper del backend da por abandonado a los 2 minutos
   * de silencio a los intentos que laten (contra 20 minutos para los que no).
   * El body `{}` es obligatorio: el AuthGuard de la API resuelve `user` y
   * `cellId` por la rama `req.body`, y sin body cae por la rama de query y no
   * encuentra el intento.
   */
  public sendHeartbeat = (id: string): Observable<void> => {
    return this.http.patch<void>(`${this.baseUrl}/${encodeURIComponent(id)}/heartbeat`, {});
  };

  /**
   * Variante keepalive del log por-intento, para contextos de unload de la
   * pestaña (pagehide / beforeunload / visibilitychange=hidden) donde el
   * HttpClient de Angular no llega a completar la request. Usa fetch con
   * `keepalive: true` (no sendBeacon: éste no permite método PATCH ni el header
   * Authorization). El token de Firebase se pasa cacheado desde la página
   * porque en el unload no hay tiempo de resolverlo async. Best-effort: no
   * garantiza entrega, la red de seguridad real es el sweeper del backend.
   */
  public addUserSimulationLogBeacon = (id: string, payload: UserSimulationLogModel, token: string): void => {
    fetch(`${this.baseUrl}/${encodeURIComponent(id)}/logs`, {
      method: 'PATCH',
      keepalive: true,
      headers: {
        ['Content-Type']: 'application/json',
        ['Authorization']: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }).catch(() => { /* best-effort en unload; no hay a quién reportar */ });
  };

  public getUserSimulationLogs = (id: string): Observable<UserSimulationLogModel[]> => {
    return this.http.get<UserSimulationLogModel[]>(`${this.baseUrl}/${encodeURIComponent(id)}/logs`);
  };
}
